/**
 * Friendly labels and target-formatter shared by every surface that lists
 * CHRO corrections (audit table, parked queue, corpus drawer). Lives in its
 * own module so a section that doesn't otherwise depend on `audit-section`
 * can format a row without pulling in the audit table component.
 *
 * The shape consumed here is the slim subset of `AuditRow` used by the
 * formatter. Callers from other endpoints (e.g. `?status=parked`) can pass
 * their own row types as long as they expose `targetKind`, `evidenceId`, and
 * `targetRef`.
 */

export interface CorrectionTargetSource {
  targetKind: string;
  evidenceId: string;
  targetRef: string | null;
}

/**
 * Friendly labels for every combination of `targetKind` × evidenceId/targetRef
 * the correction form can emit. Keeps the audit row readable for a CHRO/CEO
 * who isn't fluent in our internal dimension slugs.
 */
export const EVIDENCE_DIM_LABEL: Record<string, string> = {
  academic: "Acadêmico",
  trajectory: "Trajetória",
  tenure: "Estabilidade",
  founder: "Fundador",
};

// Indexed by both the short legacy slug (`pm`) and the long framework id
// (`project_management`) — corrections submitted from the live results
// page carry the long form, while older corpus fixtures and tests still
// use the short form.
export const SKILL_SLUG_LABEL: Record<string, string> = {
  pm: "Project management",
  // 2026-05-19: "Stakeholder management" → "Agency to influence stakeholders"
  // tracks the framework-markdown rename. Underlying id `stakeholder_management`
  // / `sm` stays the same (DB & corpus would have to be migrated otherwise).
  sm: "Agency to influence stakeholders",
  ba: "Business acumen",
  hf: "HR fluency",
  ps: "Product sense",
  project_management: "Project management",
  stakeholder_management: "Agency to influence stakeholders",
  business_acumen: "Business acumen",
  hr_fluency: "HR fluency",
  product_sense: "Product sense",
};

export const AXIS_LABEL: Record<string, string> = {
  gca: "GCA",
  compWay: "Comp Way",
  skills: "Skills",
};

export function formatCorrectionTarget(row: CorrectionTargetSource): {
  section: string;
  detail: string;
} {
  const rawDetail = row.targetRef ?? row.evidenceId;
  switch (row.targetKind) {
    case "dim":
      return {
        section: "Evidências",
        detail: EVIDENCE_DIM_LABEL[row.evidenceId] ?? row.evidenceId,
      };
    case "skill_framework":
      return {
        section: "Skills",
        detail: SKILL_SLUG_LABEL[row.evidenceId] ?? row.evidenceId,
      };
    case "skill_leveling":
      return {
        section: "Skills · leveling",
        detail: SKILL_SLUG_LABEL[row.evidenceId] ?? row.evidenceId,
      };
    case "axis_weights":
      return {
        section: "Pesos do eixo",
        detail: AXIS_LABEL[rawDetail] ?? rawDetail,
      };
    case "axis_threshold":
      return {
        section: "Corte do eixo",
        detail: AXIS_LABEL[rawDetail] ?? rawDetail,
      };
    default:
      return { section: row.targetKind, detail: row.evidenceId };
  }
}
